/**
 * EL CUADERNO DE UNA SELECCIÓN (28-09-2026).
 *
 * «Dame el cuaderno de campo solo con esa información»: el mismo cuaderno del año
 * (modelo de la Junta v9, datos de Ajustes, motor), pero con SOLO las líneas que
 * eligió el asistente (`cuaderno_seleccion`) y los valores dictados para ESTE
 * documento. Todo en memoria:
 *
 *  - la entrada se recorta a esas líneas y a sus partes (1.2 y 1.3 salen de los
 *    tratamientos que quedan, no de todo el año);
 *  - los cambios se ponen ENCIMA de las ediciones del borrador, solo en la copia;
 *  - las líneas añadidas a mano entran solo si están en la selección.
 *
 * Nada se escribe: ni el borrador del año, ni los partes, ni una versión
 * congelada (la versión oficial del año es la del cuaderno entero). PURO.
 */

import type { BorradorCuaderno } from "./borrador";
import type { EntradaCuaderno } from "./entrada";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Líneas como mucho (el mismo tope que la herramienta del asistente). */
export const MAX_LINEAS_SELECCION = 500;

export interface SeleccionCuaderno {
  /** Ids de las líneas que entran (las de `spray_log_entries` o las añadidas). */
  lineas: string[];
  /** Por línea, los valores de ESTE documento (`{ campo: valor }`, como `line_edits`). */
  ediciones: Record<string, Record<string, unknown>>;
  /** Cómo la llamó la persona. */
  titulo: string | null;
}

/** Lo que llega en la tarjeta → una selección limpia, o null si no hay (el cuaderno entero). */
export function leerSeleccion(raw: unknown): SeleccionCuaderno | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const lineas = [...new Set((Array.isArray(o.lineas) ? o.lineas : []).map(String).filter((x) => UUID.test(x)).map((x) => x.toLowerCase()))].slice(0, MAX_LINEAS_SELECCION);
  if (!lineas.length) return null;
  const dentro = new Set(lineas);
  const ediciones: Record<string, Record<string, unknown>> = {};
  const ed = o.ediciones && typeof o.ediciones === "object" && !Array.isArray(o.ediciones) ? (o.ediciones as Record<string, unknown>) : {};
  for (const [id, campos] of Object.entries(ed)) {
    const k = id.toLowerCase();
    if (!dentro.has(k) || !campos || typeof campos !== "object" || Array.isArray(campos)) continue;
    ediciones[k] = { ...(campos as Record<string, unknown>) };
  }
  const titulo = typeof o.titulo === "string" && o.titulo.trim() ? o.titulo.replace(/\s+/g, " ").trim().slice(0, 80) : null;
  return { lineas, ediciones, titulo };
}

/** Una clave estable de la selección (para la caché de la consulta). */
export function claveSeleccion(sel: SeleccionCuaderno | null): string {
  if (!sel) return "todo";
  let h = 0;
  const texto = JSON.stringify([sel.lineas, sel.ediciones]);
  for (let i = 0; i < texto.length; i++) h = (Math.imul(31, h) + texto.charCodeAt(i)) | 0;
  return `sel-${sel.lineas.length}-${(h >>> 0).toString(36)}`;
}

/** La entrada con SOLO las líneas elegidas y los partes que las tienen. */
export function entradaDeSeleccion(entrada: EntradaCuaderno, sel: SeleccionCuaderno): EntradaCuaderno {
  const dentro = new Set(sel.lineas);
  const lineas = entrada.lineas.filter((l) => dentro.has(String(l.id).toLowerCase()));
  const partes = new Set(lineas.map((l) => l.parteId));
  return { ...entrada, lineas, partes: entrada.partes.filter((p) => partes.has(p.id)) };
}

type BorradorLike = Pick<BorradorCuaderno, "answers" | "line_overrides" | "version"> & Partial<Pick<BorradorCuaderno, "line_edits" | "added_lines">>;

const objeto = (x: unknown): Record<string, unknown> => (x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : {});

/**
 * El borrador de la copia: las respuestas de siempre, las ediciones del año MÁS
 * las de la selección (mandan estas), y solo las líneas añadidas elegidas.
 */
export function borradorDeSeleccion(borrador: BorradorLike | null, sel: SeleccionCuaderno): BorradorLike {
  const dentro = new Set(sel.lineas);
  const edits: Record<string, unknown> = { ...objeto(borrador?.line_edits) };
  for (const [id, campos] of Object.entries(sel.ediciones)) {
    const previas = objeto(edits[id]);
    const nuevas: Record<string, unknown> = {};
    for (const [campo, v] of Object.entries(campos)) nuevas[campo] = { v };
    edits[id] = { ...previas, ...nuevas };
  }
  const anadidas: Record<string, unknown> = {};
  for (const [id, v] of Object.entries(objeto(borrador?.added_lines))) if (dentro.has(id.toLowerCase())) anadidas[id] = v;
  return {
    answers: borrador?.answers ?? {},
    line_overrides: borrador?.line_overrides ?? {},
    version: borrador?.version ?? 0,
    line_edits: edits,
    added_lines: anadidas,
  };
}
